// Wave 5: Settings › Connections (YouTube connect / disconnect) and the publishing kit's "Upload to YouTube" with its
// confirm sheet (title, description, privacy: private by default, the AI-content box, the Community Guidelines box).
// Hidden when the server has no Google OAuth client (GET /api/youtube says enabled: false). An upload only ever
// starts from the Post button here (never from the Director).
import { useEffect, useState } from "react";
import { callbackMessage, youtube, type YoutubeStatus, type YoutubeUpload } from "../youtube";
import { toast } from "../store";
import { Icon, Sheet, timeAgo, useLoad } from "./bits";

/** Shows the toast for the ?youtube= outcome Google's callback came back with, then drops it from the address. */
export function useYoutubeCallback(onDone?: () => void) {
  useEffect(() => {
    const m = callbackMessage(window.location.search);
    if (!m) return;
    toast(m.text, m.tone);
    const u = new URL(window.location.href);
    u.searchParams.delete("youtube");
    window.history.replaceState(window.history.state, "", `${u.pathname}${u.search}${u.hash}`);
    onDone?.();
    // once, on arrival
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

async function goConnect(returnTo: string) {
  try {
    const url = await youtube.connectUrl(returnTo);
    if (url) window.location.assign(url);
  } catch (err) {
    toast(`Couldn't start connecting YouTube. ${(err as Error).message}`, "bad");
  }
}

/** Settings › Connections. Renders nothing when YouTube publishing is off on the server. */
export function YoutubeConnection({ status, reload }: { status: YoutubeStatus | null; reload: () => void }) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  useYoutubeCallback(reload);
  if (!status?.enabled) return null;
  const disconnect = async () => {
    setAsking(false);
    setBusy(true);
    try {
      await youtube.disconnect();
      toast("YouTube is disconnected. We've removed our access and deleted the connection.", "good");
      reload();
    } catch (err) {
      toast(`Couldn't disconnect. ${(err as Error).message}`, "bad");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="group yt-connection" aria-labelledby="yt-h">
      <h2 id="yt-h" className="group-title">
        YouTube
      </h2>
      {status.connected ? (
        <>
          <p className="muted">
            Connected{status.connectedAt ? ` ${timeAgo(status.connectedAt)}` : ""}. CAO Studio can upload videos to your channel when you press Upload on a video, and nothing else.
          </p>
          <div className="confirm-actions">
            <button type="button" className="btn btn-quiet" data-action="youtube.disconnect" disabled={busy} onClick={() => setAsking(true)}>
              {busy ? "Disconnecting" : "Disconnect"}
            </button>
            <a className="btn btn-quiet" href={status.manageUrl} target="_blank" rel="noreferrer">
              Your Google account's connected apps
            </a>
          </div>
        </>
      ) : (
        <>
          <p className="muted">Connect your YouTube channel to upload finished videos straight from their Publish fold. We only ask Google for permission to upload.</p>
          <button type="button" className="btn btn-primary" data-action="youtube.connect" onClick={() => void goConnect("/settings")}>
            Connect YouTube
          </button>
        </>
      )}
      {asking && (
        <Sheet title="Disconnect YouTube?" onClose={() => setAsking(false)}>
          <div className="sheet-body confirm">
            <p>We'll tell Google to remove CAO Studio's access and delete the connection and our record of your uploads. Videos already on YouTube stay there.</p>
            <div className="confirm-actions">
              <button type="button" className="btn btn-quiet" onClick={() => setAsking(false)}>
                Keep it
              </button>
              <button type="button" className="btn btn-primary" onClick={() => void disconnect()} data-autofocus>
                Disconnect
              </button>
            </div>
          </div>
        </Sheet>
      )}
    </section>
  );
}

export type UploadDefaults = { title: string; description: string; tags: string[]; shortForm: boolean };

const PRIVACY: { id: YoutubeUpload["privacy"]; label: string; hint: string }[] = [
  { id: "private", label: "Private", hint: "Only you. Check it on YouTube, then make it public there." },
  { id: "unlisted", label: "Unlisted", hint: "Anyone with the link." },
  { id: "public", label: "Public", hint: "Everyone." },
];

/** The publishing kit's YouTube row: the button, the confirm sheet, and this video's uploads with their progress. */
export function YoutubeUpload({ projectId, version, defaults }: { projectId: string; version: number; defaults: UploadDefaults }) {
  const { data: status, reload: reloadStatus } = useLoad(() => youtube.status(), []);
  const { data: uploads, setData: setUploads, reload } = useLoad(() => (status?.enabled && status.connected ? youtube.uploads(projectId).catch(() => [] as YoutubeUpload[]) : Promise.resolve([] as YoutubeUpload[])), [projectId, status?.enabled, status?.enabled && status.connected]);
  const [open, setOpen] = useState(false);
  useYoutubeCallback(reloadStatus);
  const uploading = !!uploads?.some((u) => u.status === "uploading");
  useEffect(() => {
    if (!uploading) return;
    const t = setInterval(reload, 2500);
    return () => clearInterval(t);
  }, [uploading, reload]);
  if (!status?.enabled) return null;
  const tap = () => (status.connected ? setOpen(true) : void goConnect(window.location.pathname));
  return (
    <div className="pk-row yt-upload">
      <div className="pk-head">
        <span className="field-label">YouTube</span>
        <button type="button" className="btn btn-primary btn-small" data-action="youtube.upload" onClick={tap} disabled={uploading}>
          <Icon name="send" size={14} /> {status.connected ? "Upload to YouTube" : "Connect YouTube to upload"}
        </button>
      </div>
      {!!uploads?.length && (
        <ul className="yt-uploads">
          {uploads.slice(0, 3).map((u) => (
            <li key={u.id} className={`yt-up yt-${u.status}`}>
              <span>
                v{u.version ?? "?"} · {u.privacy}
                {u.createdAt ? ` · ${timeAgo(u.createdAt)}` : ""}
              </span>{" "}
              {u.status === "uploading" && <span className="muted">Uploading {Math.round(u.progress * 100)}%</span>}
              {u.status === "failed" && <span className="muted">{u.error ?? "Failed."}</span>}
              {u.status === "done" && u.watchUrl && (
                <a href={u.studioUrl ?? u.watchUrl} target="_blank" rel="noreferrer">
                  Open in YouTube Studio
                </a>
              )}
              {u.keptPrivate && <p className="field-hint">YouTube is keeping this private until our app review with Google finishes. You can change it in YouTube Studio once that's done.</p>}
            </li>
          ))}
        </ul>
      )}
      {open && (
        <UploadSheet
          projectId={projectId}
          version={version}
          defaults={defaults}
          onClose={() => setOpen(false)}
          onStarted={(u) => {
            setOpen(false);
            setUploads([u, ...(uploads ?? [])]);
          }}
        />
      )}
    </div>
  );
}

function UploadSheet({ projectId, version, defaults, onClose, onStarted }: { projectId: string; version: number; defaults: UploadDefaults; onClose: () => void; onStarted: (u: YoutubeUpload) => void }) {
  const shortsTag = defaults.shortForm && !/#shorts/i.test(`${defaults.title} ${defaults.description}`);
  const [title, setTitle] = useState(defaults.title.slice(0, 100));
  const [description, setDescription] = useState(shortsTag ? `${defaults.description}\n\n#Shorts`.trim() : defaults.description);
  const [privacy, setPrivacy] = useState<YoutubeUpload["privacy"]>("private");
  const [synthetic, setSynthetic] = useState(true);
  const [guidelines, setGuidelines] = useState(false);
  const [busy, setBusy] = useState(false);
  const post = async () => {
    setBusy(true);
    try {
      const u = await youtube.upload(projectId, { version, title: title.trim(), description, privacy, containsSyntheticMedia: synthetic, tags: defaults.tags, confirmGuidelines: true });
      if (u) onStarted(u);
      toast("Uploading to YouTube. You can keep working; it finishes on its own.", "good");
    } catch (err) {
      toast(`Couldn't upload. ${(err as Error).message}`, "bad");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet title="Upload to YouTube" onClose={onClose}>
      <div className="sheet-body yt-sheet">
        <label className="field">
          <span className="field-label">Title</span>
          <input value={title} maxLength={100} onChange={(e) => setTitle(e.target.value)} data-autofocus />
          <span className="field-hint">{100 - title.length} characters left</span>
        </label>
        <label className="field">
          <span className="field-label">Description</span>
          <textarea value={description} maxLength={5000} rows={6} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <fieldset className="field">
          <legend className="field-label">Who can watch</legend>
          {PRIVACY.map((p) => (
            <label key={p.id} className="yt-radio">
              <input type="radio" name="yt-privacy" checked={privacy === p.id} onChange={() => setPrivacy(p.id)} /> <strong>{p.label}</strong> <span className="muted">{p.hint}</span>
            </label>
          ))}
        </fieldset>
        <label className="yt-check">
          <input type="checkbox" checked={synthetic} onChange={(e) => setSynthetic(e.target.checked)} /> Tell YouTube it has realistic AI-made content (altered or synthetic). Leave this on unless nothing in it could pass for real.
        </label>
        <label className="yt-check">
          <input type="checkbox" checked={guidelines} onChange={(e) => setGuidelines(e.target.checked)} /> This video follows YouTube's{" "}
          <a href="https://www.youtube.com/howyoutubeworks/policies/community-guidelines/" target="_blank" rel="noreferrer">
            Community Guidelines
          </a>{" "}
          and I agree to the{" "}
          <a href="https://www.youtube.com/t/terms" target="_blank" rel="noreferrer">
            YouTube Terms of Service
          </a>
          .
        </label>
        <div className="confirm-actions">
          <button type="button" className="btn btn-quiet" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={busy || !guidelines || !title.trim()} onClick={() => void post()}>
            {busy ? "Starting" : `Post as ${privacy}`}
          </button>
        </div>
      </div>
    </Sheet>
  );
}
